import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { SIGNATURE_TRACK } from "@/constants/music";

const MusicContext = createContext(null);
const PREF_KEY = "sh_music_pref";   // "on" | "off" | null (undecided)
const MUTE_KEY = "sh_music_muted";  // "1" | "0"
const TIME_KEY = "sh_music_time";   // last playback position (seconds)

export function MusicProvider({ children }) {
  const audioRef = useRef(null);
  const duckRef = useRef({ foreign: 0, wasPlaying: false });
  const autoMutedRef = useRef(false);  // muted purely as an autoplay workaround
  const userMutedRef = useRef(false);  // the member deliberately muted
  const fadeRef = useRef(null);
  const volumeRef = useRef(SIGNATURE_TRACK.defaultVolume);
  const savedTimeRef = useRef(parseFloat(localStorage.getItem(TIME_KEY)) || 0);
  const restoredRef = useRef(false);
  const lastSaveRef = useRef(0);

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolumeState] = useState(SIGNATURE_TRACK.defaultVolume);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [blocked, setBlocked] = useState(false);
  const prefRef = useRef(localStorage.getItem(PREF_KEY));

  const setVol = useCallback((v) => { volumeRef.current = v; setVolumeState(v); }, []);

  const fadeTo = useCallback((target, ms = 1600) => {
    const a = audioRef.current;
    if (!a) return;
    if (fadeRef.current) clearInterval(fadeRef.current);
    const start = a.volume;
    const steps = Math.max(1, Math.round(ms / 40));
    let i = 0;
    fadeRef.current = setInterval(() => {
      i += 1;
      const v = start + (target - start) * (i / steps);
      a.volume = Math.min(1, Math.max(0, v));
      if (i >= steps) { clearInterval(fadeRef.current); fadeRef.current = null; a.volume = target; setVol(target); }
    }, 40);
  }, [setVol]);

  const attemptPlay = useCallback(async () => {
    const a = audioRef.current;
    if (!a) return false;
    try { await a.play(); setBlocked(false); return true; }
    catch { setBlocked(true); return false; }
  }, []);

  // Restore the last position (survives the full-page Google OAuth redirect).
  const restorePosition = useCallback(() => {
    const a = audioRef.current;
    if (!a || restoredRef.current) return;
    const t = savedTimeRef.current;
    if (t > 0 && isFinite(t) && a.duration && t < a.duration - 0.5) {
      try { a.currentTime = t; } catch (e) { console.debug("seek before ready:", e); }
    }
    restoredRef.current = true;
  }, []);

  // On mount: RESTORE the saved position first, then resume. Try audible (media
  // engagement usually permits it after prior interaction such as the Google
  // sign-in click); otherwise start muted + unlock on the first gesture.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0;
    a.muted = false;
    if (prefRef.current === "off") return;
    if (!sessionStorage.getItem("sh_entered")) return; // wait for the Enter screen — no music until ENTER
    const start = async () => {
      restorePosition();            // seek to the saved time BEFORE playing
      try {
        await a.play();
        setBlocked(false);
        fadeTo(volumeRef.current || SIGNATURE_TRACK.defaultVolume, 1200);
      } catch {
        try { a.muted = true; autoMutedRef.current = true; setMuted(true); await a.play(); } catch (e) { console.debug("muted autoplay fallback blocked:", e); }
        setBlocked(true);
      }
    };
    if (a.readyState >= 1 && a.duration) start();
    else a.addEventListener("loadedmetadata", start, { once: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any interaction makes it audible from the current (restored) position, fading up.
  // Listeners PERSIST and retry on every interaction until playback is truly audible,
  // so an early spurious event (e.g. a mousemove during load, or the post-Google-redirect
  // navigation) can't waste the one-and-only unlock attempt.
  useEffect(() => {
    if (prefRef.current === "off") return;
    const evs = ["pointerdown", "keydown", "touchstart", "scroll", "mousemove", "wheel", "click"];
    const cleanup = () => evs.forEach((e) => window.removeEventListener(e, unlock, true));
    const unlock = () => {
      const a = audioRef.current;
      if (!a) return;
      if (!sessionStorage.getItem("sh_entered")) return; // nothing until the member hits ENTER
      if (prefRef.current === "off") { cleanup(); return; }
      if (!userMutedRef.current) { a.muted = false; autoMutedRef.current = false; setMuted(false); }
      a.play().then(() => {
        setBlocked(false);
        if (a.volume < 0.01) fadeTo(volumeRef.current || SIGNATURE_TRACK.defaultVolume, 1000);
        if (!a.paused && !a.muted) cleanup(); // stop listening only once truly audible
      }).catch(() => { /* not ready / still blocked — keep listening for the next interaction */ });
    };
    evs.forEach((e) => window.addEventListener(e, unlock, { capture: true, passive: true }));
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the position on unload so Google OAuth (and any reload) can resume seamlessly.
  useEffect(() => {
    const save = () => {
      const a = audioRef.current;
      if (a) localStorage.setItem(TIME_KEY, String(a.currentTime || 0));
    };
    window.addEventListener("pagehide", save);
    window.addEventListener("beforeunload", save);
    document.addEventListener("visibilitychange", save);
    return () => {
      window.removeEventListener("pagehide", save);
      window.removeEventListener("beforeunload", save);
      document.removeEventListener("visibilitychange", save);
    };
  }, []);

  // Other media (course video, coaching/meditation/wellness/journal audio) takes priority.
  useEffect(() => {
    const isForeign = (t) => t && t !== audioRef.current && (t.tagName === "AUDIO" || t.tagName === "VIDEO");
    const onPlay = (e) => {
      if (!isForeign(e.target)) return;
      const d = duckRef.current;
      if (d.foreign === 0) {
        d.wasPlaying = !!(audioRef.current && !audioRef.current.paused);
        if (audioRef.current) audioRef.current.pause();
      }
      d.foreign += 1;
    };
    const onStop = (e) => {
      if (!isForeign(e.target)) return;
      const d = duckRef.current;
      d.foreign = Math.max(0, d.foreign - 1);
      if (d.foreign === 0 && d.wasPlaying && prefRef.current !== "off") attemptPlay();
    };
    document.addEventListener("play", onPlay, true);
    document.addEventListener("pause", onStop, true);
    document.addEventListener("ended", onStop, true);
    return () => {
      document.removeEventListener("play", onPlay, true);
      document.removeEventListener("pause", onStop, true);
      document.removeEventListener("ended", onStop, true);
    };
  }, [attemptPlay]);

  const play = useCallback(() => {
    prefRef.current = "on";
    localStorage.setItem(PREF_KEY, "on");
    return attemptPlay();
  }, [attemptPlay]);

  const pause = useCallback(() => {
    prefRef.current = "off";
    localStorage.setItem(PREF_KEY, "off");
    if (audioRef.current) audioRef.current.pause();
  }, []);

  const toggle = useCallback(() => { playing ? pause() : play(); }, [playing, play, pause]);

  const toggleMute = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = !a.muted;
    autoMutedRef.current = false;
    userMutedRef.current = a.muted;
    setMuted(a.muted);
    localStorage.setItem(MUTE_KEY, a.muted ? "1" : "0");
    if (!a.muted && a.volume < 0.01) fadeTo(volumeRef.current || SIGNATURE_TRACK.defaultVolume, 600);
  }, [fadeTo]);

  const changeVolume = useCallback((v) => {
    const a = audioRef.current;
    if (fadeRef.current) { clearInterval(fadeRef.current); fadeRef.current = null; }
    if (a) {
      a.volume = v;
      if (a.muted && v > 0) { a.muted = false; autoMutedRef.current = false; userMutedRef.current = false; setMuted(false); localStorage.setItem(MUTE_KEY, "0"); }
    }
    setVol(v);
  }, [setVol]);

  const seek = useCallback((t) => {
    const a = audioRef.current;
    if (a && isFinite(t)) { a.currentTime = t; setCurrentTime(t); localStorage.setItem(TIME_KEY, String(t)); }
  }, []);

  // ENTER / "Enter with sound": begin the track from the top, unmuted, fading in.
  const enableWithSound = useCallback(() => {
    const a = audioRef.current;
    prefRef.current = "on";
    localStorage.setItem(PREF_KEY, "on");
    if (!a) return;
    userMutedRef.current = false;
    autoMutedRef.current = false;
    restoredRef.current = true; // fresh start — don't let restore override
    a.muted = false;
    setMuted(false);
    localStorage.setItem(MUTE_KEY, "0");
    try { a.currentTime = 0; } catch (e) { /* noop */ }
    localStorage.setItem(TIME_KEY, "0");
    a.volume = 0;
    a.play().then(() => setBlocked(false)).catch(() => setBlocked(true));
    fadeTo(volumeRef.current || SIGNATURE_TRACK.defaultVolume, 1800);
  }, [fadeTo]);

  // Resume from the saved position (used right after the Google OAuth return).
  const resume = useCallback(() => {
    const a = audioRef.current;
    if (!a || prefRef.current === "off") return;
    const go = () => {
      restorePosition();
      if (!userMutedRef.current) { a.muted = false; autoMutedRef.current = false; setMuted(false); }
      a.play()
        .then(() => { setBlocked(false); if (a.volume < 0.01) fadeTo(volumeRef.current || SIGNATURE_TRACK.defaultVolume, 1000); })
        .catch(() => setBlocked(true));
    };
    if (a.readyState >= 2) go();
    else a.addEventListener("canplay", go, { once: true });
  }, [fadeTo, restorePosition]);

  // "Keep playing" — an explicit user request for sound. Unlike resume(), this
  // overrides a prior "off" preference and unmutes, since the member just asked for it.
  const keepPlaying = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    prefRef.current = "on";
    localStorage.setItem(PREF_KEY, "on");
    const go = () => {
      restorePosition();
      userMutedRef.current = false;
      autoMutedRef.current = false;
      a.muted = false;
      setMuted(false);
      localStorage.setItem(MUTE_KEY, "0");
      a.play()
        .then(() => { setBlocked(false); if (a.volume < 0.01) fadeTo(volumeRef.current || SIGNATURE_TRACK.defaultVolume, 1000); })
        .catch(() => setBlocked(true));
    };
    if (a.readyState >= 2) go();
    else a.addEventListener("canplay", go, { once: true });
  }, [fadeTo, restorePosition]);

  const value = {
    track: SIGNATURE_TRACK,
    playing, muted, volume, currentTime, duration, blocked,
    audible: playing && !muted,
    toggle, play, pause, toggleMute, changeVolume, seek, enableWithSound, resume, keepPlaying,
  };

  return (
    <MusicContext.Provider value={value}>
      <audio
        ref={audioRef}
        src={SIGNATURE_TRACK.src}
        loop={SIGNATURE_TRACK.loop}
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={(e) => { setDuration(e.currentTarget.duration || 0); restorePosition(); }}
        onTimeUpdate={(e) => {
          const t = e.currentTarget.currentTime || 0;
          setCurrentTime(t);
          const now = Date.now();
          if (t > 0.25 && now - lastSaveRef.current > 1000) { lastSaveRef.current = now; localStorage.setItem(TIME_KEY, String(t)); }
        }}
      />
      {children}
    </MusicContext.Provider>
  );
}

export const useMusic = () => useContext(MusicContext);
